import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Divider,
  Collapse,
  Modal,
  Form,
  Input,
  Button,
  Checkbox,
  Select,
  Switch,
  DatePicker,
  message,
  Spin,
} from "antd";
import moment from "moment";
import EditCandidateIcon from "../../../Assets/editCandidateIcon.svg";
import DeleteIcon from "../../../Assets/delete.svg";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";
import API_MANAGER from "../../../API";

// Toggle this to true when you want auto-refresh after update
const AUTO_RELOAD_AFTER_UPDATE = false;

function JobPositionCard({ data, discipline, induction, industry, skills, profileId, jobTitle, onUpdate, userId }) {
  const { type } = useParams();
  const [expForm] = Form.useForm();
  const [showReferee, setShowReferee] = useState(data?.hasReferee);
  const [loading, setLoading] = useState(false);
  const [expEdit, setExpEdit] = useState(false);
  const [experience, setExperience] = useState(data);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [currentRoleStatus, setCurrentRoleStatus] = useState(data?.currentRole || false);
  const handleUpdate = async (values) => {
    try {
      setLoading(true);
      const payload = { ...values };
      payload.profileId = profileId;
      payload.experiencId = data._id;

      // Normalize dates to ISO strings for API
      if (payload.startDate) {
        payload.startDate = dayjs(payload.startDate).toISOString();
      }
      if (payload.currentRole) {
        payload.endDate = null;
      } else if (payload.endDate) {
        payload.endDate = dayjs(payload.endDate).toISOString();
      }

      await API_MANAGER.editCandidateExperience(payload, data._id);
      message.success('Successfully Edited Experience');
      setExpEdit(false);

      // Don't update local state immediately - let onUpdate refresh from server
      // This prevents duplicate cards and ensures we have the latest data
      if (onUpdate) {
        await onUpdate();
      }
      
      // After refresh, the useEffect will update the experience state from props
      if (AUTO_RELOAD_AFTER_UPDATE) {
        window.location.reload();
      }
    } catch (error) {
      message.error(error?.response?.data?.message?.message)
    } finally {
      setLoading(false);
    }
  };
  const setFields = () => {
    const currentRole = experience?.currentRole || false;
    setCurrentRoleStatus(currentRole);
    expForm.setFieldsValue({
      employerName: experience?.employerName,
      projectName: experience?.projectName,
      industry: experience?.industry?._id || experience?.industry?.id,
      discipline: experience?.discipline?._id || experience?.discipline?.id,
      inductions: experience?.inductions?.map((item) => item?.id || item?._id),
      skills: experience?.skills?.map((item) => item?.id || item?._id),
      refereeName: experience?.refereeName,
      refereePosition: experience?.refereePosition,
      refereeContact: experience?.refereeContact,
      jobTitle: experience?.jobTitle?._id || experience?.jobTitle?.id,
      hasReferee: experience?.hasReferee,
      currentRole: currentRole,
      startDate: experience?.startDate ? dayjs(experience.startDate) : null,
      endDate: currentRole ? null : (experience?.endDate ? dayjs(experience.endDate) : null),
    });
    setShowReferee(experience?.hasReferee);
  };
  useEffect(() => {
    if (data) {
      // Avoid overwriting freshly updated local state with stale props
      const incomingUpdatedAt = data?.updatedAt ? new Date(data.updatedAt).getTime() : 0;
      const localUpdatedAt = experience?.updatedAt ? new Date(experience.updatedAt).getTime() : 0;
      if (incomingUpdatedAt >= localUpdatedAt || !localUpdatedAt) {
        setExperience(data);
        setCurrentRoleStatus(data?.currentRole || false);
      }
    }
  }, [data]);

  // Re-populate form whenever local experience changes
  useEffect(() => {
    setFields();
  }, [experience]);
  return (
    <>
      <div className="jobPositionCard">
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "5px",
          }}
        >
          {type === "edit" ? (
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <img
                src={DeleteIcon}
                alt="delete"
                style={{ 
                  width: "35px", 
                  height: "35px",
                  cursor: "pointer",
                  filter: "none",
                  background: "transparent",
                  border: "none",
                  padding: 0
                }}
                onClick={() => {
                  setDeleteModalVisible(true);
                }}
              />
              <img src={EditCandidateIcon} onClick={() => setExpEdit(true)} style={{ cursor: 'pointer' }} />
            </div>
          ) : (
            ""
          )}
        </div>
        <Row justify={"space-between"} align={"middle"}>
          <Col>
            <p className="title">{experience?.jobTitle?.name}</p>
          </Col>
          {experience?.currentRole && (
            <Col>
              <div className="currentRole">Current Role</div>
            </Col>
          )}
        </Row>
        <div className="mt-20">
          <Row gutter={8}>
            <Col span={16}>
              <p className="item">
                Start Date:{" "}
                <span>{moment(experience?.startDate)?.format("MMM YYYY")}</span>
              </p>
            </Col>
            <Col span={8}>
              <p className="item">
                End Date:{" "}
                <span>
                  {experience?.endDate
                    ? moment(experience?.endDate)?.format("MMM YYYY")
                    : "----"}
                </span>
              </p>
            </Col>
          </Row>
        </div>
        <Divider className="horizontalDivider" />
        <div>
          <Row gutter={16} className="itemRow">
            <Col span={8}>
              <p className="item">Employer</p>
              <p className="value">{experience?.employerName}</p>
            </Col>
            <Col span={8}>
              <p className="item">Project Name</p>
              <p className="value">{experience?.projectName}</p>
            </Col>
            <Col span={8}>
              <p className="item">Industry</p>
              <p className="value">{experience?.industry?.name}</p>
            </Col>
          </Row>
          <Row gutter={16} className="itemRow">
            <Col span={8}>
              <p className="item">Discipline</p>
              <p className="value">{experience?.discipline?.name}</p>
            </Col>
            <Col span={8}>
              <p className="item">Inductions</p>
              <p className="value">
                {experience?.inductions?.map((item, index) => (
                  <span key={index}>
                    {index === 0 ? (
                      item?.name
                    ) : (
                      <>
                        {`, `}
                        <br />
                        {item?.name}
                      </>
                    )}
                  </span>
                ))}
              </p>
            </Col>
            <Col span={8}>
              <p className="item">Skills</p>
              <p className="value">
                {experience?.skills?.map((item, index) => (
                  <span key={index}>
                    {index === 0 ? (
                      item?.name
                    ) : (
                      <>
                        {`, `}
                        <br />
                        {item?.name}
                      </>
                    )}
                  </span>
                ))}
              </p>
            </Col>
          </Row>
        </div>
        {experience?.hasReferee && (
          <div>
            <Divider className="horizontalDivider" />
            <div className="collapseContainer">
              <Collapse
                // size="small"
                expandIconPosition={"end"}
                bordered={false}
                items={[
                  {
                    key: "1",
                    label: "Referee Details ",
                    children: (
                      <Row gutter={16} className="itemRow">
                        <Col span={8}>
                          <p className="item">Referee Name</p>
                          <p className="value">{experience?.refereeName}</p>
                        </Col>
                        <Col span={8}>
                          <p className="item">Referee Position</p>
                          <p className="value">{experience?.refereePosition}</p>
                        </Col>
                        <Col span={8}>
                          <p className="item">Referee Email/Phone No.</p>
                          <p className="value">{experience?.refereeContact}</p>
                        </Col>
                      </Row>
                    ),
                  },
                ]}
              />
            </div>
          </div>
        )}
      </div>
      <Modal
        centered
        footer={false}
        open={expEdit}
        title={"Professional Experience Edit"}
        className="resumeModal"
        onCancel={() => setExpEdit(false)}
      >
        <Form form={expForm} layout="vertical" requiredMark={false} onFinish={handleUpdate}>
          <Spin spinning={loading} />
          <Row gutter={24}>
            <Col xs={12}>
              <Form.Item 
                label="Employer*" 
                name="employerName"
                requiredMark={false}
                rules={[
                  {
                    required: true,
                    message: "Please enter employer name",
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item label="Project Name" name="projectName">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col xs={12}>
              <Form.Item label="Job Title*" name="jobTitle">
                <Select className="" placeholder="industry">
                  {jobTitle?.map((e, key) => {
                    return (
                      <Select.Option key={key} value={e.id}>
                        {e.name}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item label="Industry*" name="industry">
                <Select className="" placeholder="industry">
                  {industry?.map((e, key) => {
                    return (
                      <Select.Option key={key} value={e.id}>
                        {e.name}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col xs={12}>
              <Form.Item label="Discipline*" name="discipline">
                <Select className="" placeholder="discipline">
                  {discipline?.map((e, key) => {
                    return (
                      <Select.Option key={key} value={e.id}>
                        {e.name}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item label="Inductions*" name="inductions">
                <Select
                  className=""
                  mode="multiple"
                  maxTagCount="responsive"
                  placeholder="induction"
                  filterOption={(input, option) =>
                    option.props.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {induction?.map((e, key) => {
                    return (
                      <Select.Option key={key} value={e.id}>
                        {e.name}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col xs={12}>
              <Form.Item
                label="Job Description, Responsibilties, Skills*"
                name="skills"
              >
                <Select
                  className=""
                  mode="multiple"
                  maxTagCount="responsive"
                  placeholder="skills"
                  filterOption={(input, option) =>
                    option.props.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {skills?.map((e, key) => {
                    return (
                      <Select.Option key={key} value={e.id}>
                        {e.name}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item label="Start Date*" name="startDate">
                <DatePicker
                  className="w-100"
                  format={"DD-MM-YYYY"}
                  placeholder={"Select start date"}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col xs={12}>
              <Form.Item 
                label={currentRoleStatus ? "End Date" : "End Date*"} 
                name="endDate"
                rules={[
                  {
                    required: !currentRoleStatus,
                    message: 'Please select end date'
                  }
                ]}
              >
                <DatePicker
                  className="w-100"
                  format={"DD-MM-YYYY"}
                  placeholder={"Select end date"}
                  disabled={currentRoleStatus}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col xs={12}>
              <Form.Item name="hasReferee" valuePropName="checked">
                <Checkbox
                  onChange={(e) => setShowReferee(e.target.checked)}
                >
                  Referee Details
                </Checkbox>
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item name="currentRole" valuePropName="checked">
                <span style={{ marginRight: "10px" }}>Current Role</span>
                <Switch
                  checked={currentRoleStatus}
                  onChange={(checked) => {
                    setCurrentRoleStatus(checked);
                    expForm.setFieldsValue({ currentRole: checked });
                    if (checked) {
                      expForm.setFieldsValue({ endDate: null });
                    }
                    setTimeout(() => {
                      expForm.validateFields(['endDate']);
                    }, 0);
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          {showReferee ? (
            <>
              <Row gutter={24}>
                <Col xs={12}>
                  <Form.Item 
                    label="Referee Name*" 
                    name="refereeName"
                    rules={[
                      {
                        required: true,
                        message: "Please enter referee name",
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item 
                    label="Referee Position*" 
                    name="refereePosition"
                    rules={[
                      {
                        required: true,
                        message: "Please enter referee position",
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={24}>
                <Col xs={12}>
                  <Form.Item
                    label="Referee Email/ Phone Number*"
                    name="refereeContact"
                    rules={[
                      {
                        required: true,
                        message: "Please enter referee email or phone number",
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>{" "}
            </>
          ) : (
            ""
          )}

          <Row>
            <Col xs={24} className="buttonCol">
              <Button className="btn1" htmlType="submit">
                Update
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>
      <Modal
        className="candidateDeleteModal"
        centered
        onCancel={() => {
          setDeleteModalVisible(false);
        }}
        open={deleteModalVisible}
        footer={null}
        title="Delete Experience?"
      >
        <Row className="header">
          <Col>
            <p className="desc">
              Are you sure you want to delete this experience?
            </p>
          </Col>
        </Row>

        <Row align={"middle"} gutter={16} justify={"end"}>
          <Col>
            <Button
              className="confirmBtn"
              onClick={async () => {
                try {
                  setLoading(true);
                  await API_MANAGER.deleteCandidateExperience(
                    data._id || data.id,
                    userId
                  );
                  message.success("Experience deleted successfully");
                  setDeleteModalVisible(false);
                  if (onUpdate) {
                    await onUpdate();
                  }
                } catch (error) {
                  message.error(
                    error?.response?.data?.message?.message || "Failed to delete experience"
                  );
                } finally {
                  setLoading(false);
                }
              }}
              loading={loading}
            >
              Delete
            </Button>
          </Col>
          <Col>
            <Button
              className="cancelBtn"
              onClick={() => {
                setDeleteModalVisible(false);
              }}
            >
              Cancel
            </Button>
          </Col>
        </Row>
      </Modal>
    </>
  );
}

export default JobPositionCard;
